import { medal } from '@alison/ui';
import type { SvgIconComponent } from '@mui/icons-material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { forwardRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

interface ControlButtonProps {
  label: string;
  value: ReactNode;
  open: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  tone?: string;
  icon?: SvgIconComponent;
  onRemove?: () => void;
  placeholder?: boolean;
  labelFirst?: boolean;
  fixedWidth?: boolean;
  width?: number;
  disabled?: boolean;
  warning?: boolean;
  centered?: boolean;
}

export const CHIP_WIDTH = 230;

export const ControlButton = forwardRef<HTMLButtonElement, ControlButtonProps>(
  function ControlButton(
    {
      label,
      value,
      open,
      onClick,
      tone,
      icon: Icon,
      onRemove,
      placeholder,
      labelFirst = false,
      fixedWidth = false,
      width,
      disabled = false,
      warning = false,
      centered = false,
    },
    ref,
  ) {
    const labelText = warning ? (
      <WarningAmberOutlined sx={{ fontSize: 14, color: medal.gold, flexShrink: 0 }} />
    ) : (
      <Box
        component="span"
        sx={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.disabled',
          flexShrink: 0,
        }}
      >
        {label}
      </Box>
    );

    const valueText = (
      <Tooltip
        title={disabled || typeof value !== 'string' ? '' : value}
        placement="top"
        disableInteractive
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
          },
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            color: placeholder ? 'text.disabled' : warning ? medal.gold : 'text.primary',
            display: 'inline-block',
            flex: centered ? '0 1 auto' : 1,
            minWidth: 0,
            ...(fixedWidth
              ? { textAlign: 'left' }
              : centered
                ? { maxWidth: 160, textAlign: 'center' }
                : { maxWidth: 160 }),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'bottom',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Box>
      </Tooltip>
    );

    const inlineIcon = Icon ? (
      <Icon sx={{ alignSelf: 'center', fontSize: 14, color: 'text.secondary' }} />
    ) : null;

    return (
      <Box
        ref={ref}
        component="button"
        type="button"
        onClick={onClick}
        disabled={disabled}
        {...(warning
          ? { 'aria-label': `${label} ${typeof value === 'string' ? value : ''}`.trim() }
          : {})}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          height: 34,
          minWidth: 0,
          ...(fixedWidth ? { width: width ?? CHIP_WIDTH } : {}),
          px: 2,
          border: '1px solid',
          borderColor: open
            ? theme.palette.primary.main
            : warning
              ? medal.gold
              : theme.palette.border.strong,
          borderRadius: 1,
          bgcolor: warning ? theme.palette.warning.bg : 'background.paper',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
          '&:hover': disabled
            ? {}
            : { borderColor: warning ? medal.gold : theme.palette.border.primary },
        })}
        aria-expanded={open}
      >
        {labelFirst ? (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            {labelText}
            {inlineIcon}
            {valueText}
          </Box>
        ) : (
          <>
            {tone ? (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: tone,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 2,
                flex: 1,
                minWidth: 0,
              }}
            >
              {labelText}
              {valueText}
            </Box>
          </>
        )}
        <ExpandMoreOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
        {onRemove ? (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            aria-label={`Remove ${label} filter`}
            onClick={(event: MouseEvent<HTMLSpanElement>) => {
              event.stopPropagation();
              onRemove();
            }}
            onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
                event.preventDefault();
                onRemove();
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: 1,
              ml: 1,
              color: 'text.disabled',
              '&:hover': { bgcolor: 'background.muted', color: 'error.main' },
            }}
          >
            <CloseOutlined sx={{ fontSize: 12 }} />
          </Box>
        ) : null}
      </Box>
    );
  },
);
